/**
 * Cards the agent can put into the conversation ([SHOW_SAVINGS], [SHOW_PRICING], [SHOW_SAFETY],
 * [SHOW_FEATURES]). The facts match the website and the checkout prices (license-panel.tsx);
 * replaced on 30.10.2026 the old English cards with "50K+ users", "<0.1 %" and €49/€99 plans.
 */
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import { useEntry } from '../entry-context';

export const SavingsCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <Box className={classes.card}>
      <Typography className={classes.title}>{t('entry.savings.title')}</Typography>
      <Box className={classes.barRow}>
        <span className={classes.barLabel}>{t('entry.savings.manual')}</span>
        <Box className={classes.barTrack}>
          <Box className={classes.barManual} />
        </Box>
        <b className={classes.barValue}>{t('entry.savings.manualValue')}</b>
      </Box>
      <Box className={classes.barRow}>
        <span className={classes.barLabel}>{t('entry.savings.agent')}</span>
        <Box className={classes.barTrack}>
          <Box className={classes.barAgent} />
        </Box>
        <b className={classes.barValue}>{t('entry.savings.agentValue')}</b>
      </Box>
      <Typography className={classes.footLine}>{t('entry.savings.you')}</Typography>
    </Box>
  );
};

const PRICES = [
  { key: 'recruiting', price: '139,90 €' },
  { key: 'sales', price: '139,90 €' },
  { key: 'bundle', price: '249,90 €' },
] as const;

export const PricingCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { openAuth } = useEntry();
  return (
    <Box className={classes.card}>
      <Typography className={classes.title}>{t('entry.pricing.title')}</Typography>
      <Box className={classes.priceGrid}>
        {PRICES.map((p) => (
          <Box key={p.key} className={classes.priceCell}>
            <span className={classes.priceName}>{t(`entry.pricing.${p.key}`)}</span>
            <span className={classes.priceValue}>{p.price}</span>
            <span className={classes.priceUnit}>{t('entry.pricing.perMonth')}</span>
          </Box>
        ))}
      </Box>
      <Typography className={classes.note}>{t('entry.pricing.note')}</Typography>
      <Button variant="contained" className={classes.cta} onClick={() => openAuth('signup', 'pricing_card')}>
        {t('entry.pricing.cta')}
      </Button>
    </Box>
  );
};

const CheckList: React.FC<{ items: string[] }> = ({ items }) => {
  const { classes } = useStyles();
  return (
    <Box className={classes.list}>
      {items.map((text) => (
        <Box key={text} className={classes.listRow}>
          <CheckIcon className={classes.check} fontSize="small" />
          <span>{text}</span>
        </Box>
      ))}
    </Box>
  );
};

export const SafetyCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <Box className={classes.card}>
      <Typography className={classes.title}>{t('entry.safety.title')}</Typography>
      <CheckList items={[t('entry.safety.1'), t('entry.safety.2'), t('entry.safety.3'), t('entry.safety.4')]} />
      <Typography className={classes.note}>{t('entry.safety.note')}</Typography>
    </Box>
  );
};

export const StepsCard: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <Box className={classes.card}>
      <Typography className={classes.title}>{t('entry.steps.title')}</Typography>
      <Box className={classes.list}>
        {[1, 2, 3, 4].map((n) => (
          <Box key={n} className={classes.stepRow}>
            <span className={classes.stepNumber}>{n}</span>
            <span>{t(`entry.steps.${n}`)}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 14,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    maxWidth: 520,
    width: '100%',
    boxSizing: 'border-box',
  },
  title: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    fontSize: '0.85rem',
  },
  barLabel: {
    width: 110,
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  barTrack: {
    flex: 1,
    minWidth: 0,
  },
  barManual: {
    height: 10,
    borderRadius: 5,
    backgroundColor: `${theme.palette.primary.main}33`,
    width: '100%',
  },
  barAgent: {
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
    width: '13%',
  },
  barValue: {
    width: 64,
    textAlign: 'right',
    color: theme.palette.text.primary,
  },
  footLine: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  priceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: theme.spacing(1),
  },
  priceCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: theme.spacing(1.25),
    borderRadius: 10,
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  priceName: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  priceValue: {
    fontSize: '1.15rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  priceUnit: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  note: {
    fontSize: '0.8rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  cta: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  listRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    fontSize: '0.875rem',
    lineHeight: 1.45,
    color: theme.palette.text.primary,
  },
  check: {
    color: theme.palette.success.main,
    marginTop: 1,
  },
  stepRow: {
    display: 'flex',
    gap: theme.spacing(1.25),
    alignItems: 'center',
    fontSize: '0.875rem',
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 10,
    backgroundColor: theme.palette.backgrounds.main_new,
    color: theme.palette.text.primary,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
}));
